import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dailyRate", "enhancement", "risk", "tax",
    "sections", "section",
    "item", "bestCase", "likelyCase", "worstCase", "itemExpected", "itemCost",
    "sectionBest", "sectionLikely", "sectionWorst", "sectionExpected", "sectionCost",
    "riskToggle", "destroyField",
    "totalDays", "totalCost", "contingencyDays", "contingencyCost",
    "taxAmount", "grandTotal", "grandTotalSummary",
    "breakdown", "breakdownCard",
    "pieChart", "pieLegend",
    "totalItems", "totalSections", "baseDays", "baseCost", "contingencyCost2", "avgDailyCost",
    "itemsBody"
  ]

  static values = {
    dailyRate: { type: Number, default: 0 },
    enhancement: { type: Number, default: 0.15 },
    risk: { type: Number, default: 0.10 },
    tax: { type: Number, default: 0.10 }
  }

  // Palette for pie chart and breakdown (hex values for SVG, class names for bars)
  static COLORS = [
    { hex: "#3b82f6", bg: "bg-blue-500" },
    { hex: "#10b981", bg: "bg-emerald-500" },
    { hex: "#f59e0b", bg: "bg-amber-500" },
    { hex: "#ef4444", bg: "bg-red-500" },
    { hex: "#8b5cf6", bg: "bg-violet-500" },
    { hex: "#ec4899", bg: "bg-pink-500" },
    { hex: "#14b8a6", bg: "bg-teal-500" },
    { hex: "#f97316", bg: "bg-orange-500" },
    { hex: "#6366f1", bg: "bg-indigo-500" },
    { hex: "#84cc16", bg: "bg-lime-500" }
  ]

  connect() {
    this.recalculate()
  }

  updateVariables() {
    if (this.hasDailyRateTarget) this.dailyRateValue = parseFloat(this.dailyRateTarget.value) || 0
    if (this.hasEnhancementTarget) this.enhancementValue = parseFloat(this.enhancementTarget.value) || 0
    if (this.hasRiskTarget) this.riskValue = parseFloat(this.riskTarget.value) || 0
    if (this.hasTaxTarget) this.taxValue = parseFloat(this.taxTarget.value) || 0
    this.recalculate()
  }

  addSection() {
    const ts = Date.now()
    const sectionsContainer = this.element.querySelector("[data-estimate-target='sections']")
    const sectionCount = sectionsContainer.querySelectorAll("[data-estimate-target='section']").length

    const html = `
      <div class="card mb-4" data-estimate-target="section">
        <div class="card-body p-0">
          <div class="flex items-center justify-between px-4 py-3 bg-surface-secondary/50 border-b border-border">
            <div class="flex items-center gap-3 flex-1">
              <input type="hidden" name="estimate[estimate_sections_attributes][${ts}][position]" value="${sectionCount + 1}">
              <input type="text" name="estimate[estimate_sections_attributes][${ts}][name]" value="Nuova sezione"
                class="input input-sm font-semibold bg-transparent border-transparent hover:border-border focus:border-primary w-auto flex-1 max-w-xs">
              <label class="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                <input type="hidden" name="estimate[estimate_sections_attributes][${ts}][apply_risk_contingency]" value="0">
                <input type="checkbox" name="estimate[estimate_sections_attributes][${ts}][apply_risk_contingency]" value="1"
                  class="checkbox checkbox-sm" data-action="change->estimate#recalculate" data-estimate-target="riskToggle">
                <span class="hidden sm:inline">Applica rischio tecnico</span>
                <span class="sm:hidden">Rischio</span>
              </label>
            </div>
            <button type="button" class="btn btn-ghost btn-sm btn-square text-text-secondary hover:text-error"
                    data-action="click->estimate#removeSection">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
            <input type="hidden" name="estimate[estimate_sections_attributes][${ts}][_destroy]" value="0" data-estimate-target="destroyField">
          </div>

          <div class="overflow-x-auto">
            <table class="table table-sm">
              <thead>
                <tr class="text-xs">
                  <th class="w-2/5">Descrizione</th>
                  <th class="text-right w-[80px]">Ottimistico</th>
                  <th class="text-right w-[80px]">Probabile</th>
                  <th class="text-right w-[80px]">Pessimistico</th>
                  <th class="text-right w-[80px]">Stimato</th>
                  <th class="text-right w-[100px]">Costo</th>
                  <th class="w-[40px]"></th>
                </tr>
              </thead>
              <tbody data-estimate-target="itemsBody">
              </tbody>
              <tfoot>
                <tr class="font-semibold bg-surface-secondary/30">
                  <td>Subtotale</td>
                  <td class="text-right font-mono" data-estimate-target="sectionBest">0</td>
                  <td class="text-right font-mono" data-estimate-target="sectionLikely">0</td>
                  <td class="text-right font-mono" data-estimate-target="sectionWorst">0</td>
                  <td class="text-right font-mono" data-estimate-target="sectionExpected">0</td>
                  <td class="text-right font-mono" data-estimate-target="sectionCost">0</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div class="px-4 py-2 border-t border-border">
            <button type="button" class="btn btn-ghost btn-sm gap-1.5 text-text-secondary"
                    data-action="click->estimate#addItem">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-3.5 w-3.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Aggiungi voce
            </button>
          </div>
        </div>
      </div>
    `

    sectionsContainer.insertAdjacentHTML("beforeend", html)
    this.recalculate()
  }

  addItem(event) {
    const sectionEl = event.currentTarget.closest("[data-estimate-target='section']")
    const tbody = sectionEl.querySelector("[data-estimate-target='itemsBody']")
    const ts = Date.now()

    const sectionInput = sectionEl.querySelector("input[name*='estimate_sections_attributes']")
    const match = sectionInput?.name.match(/estimate_sections_attributes\]\[([^\]]+)\]/)
    const sectionIndex = match ? match[1] : ts

    const itemCount = tbody.querySelectorAll("[data-estimate-target='item']").length

    const html = `
      <tr data-estimate-target="item">
        <td>
          <input type="hidden" name="estimate[estimate_sections_attributes][${sectionIndex}][estimate_items_attributes][${ts}][position]" value="${itemCount + 1}">
          <input type="text" name="estimate[estimate_sections_attributes][${sectionIndex}][estimate_items_attributes][${ts}][description]"
            class="input input-sm w-full bg-transparent border-transparent hover:border-border focus:border-primary"
            placeholder="Descrizione attività...">
        </td>
        <td class="text-right">
          <input type="number" name="estimate[estimate_sections_attributes][${sectionIndex}][estimate_items_attributes][${ts}][best_case]"
            step="0.25" min="0" value="0"
            class="input input-sm w-[70px] text-right font-mono bg-transparent border-transparent hover:border-border focus:border-primary"
            data-action="input->estimate#recalculate" data-estimate-target="bestCase">
        </td>
        <td class="text-right">
          <input type="number" name="estimate[estimate_sections_attributes][${sectionIndex}][estimate_items_attributes][${ts}][likely_case]"
            step="0.25" min="0" value="0"
            class="input input-sm w-[70px] text-right font-mono bg-transparent border-transparent hover:border-border focus:border-primary"
            data-action="input->estimate#recalculate" data-estimate-target="likelyCase">
        </td>
        <td class="text-right">
          <input type="number" name="estimate[estimate_sections_attributes][${sectionIndex}][estimate_items_attributes][${ts}][worst_case]"
            step="0.25" min="0" value="0"
            class="input input-sm w-[70px] text-right font-mono bg-transparent border-transparent hover:border-border focus:border-primary"
            data-action="input->estimate#recalculate" data-estimate-target="worstCase">
        </td>
        <td class="text-right font-mono text-text-secondary" data-estimate-target="itemExpected">0.00</td>
        <td class="text-right font-mono font-medium" data-estimate-target="itemCost">0</td>
        <td class="text-right">
          <button type="button" class="btn btn-ghost btn-xs btn-square text-text-secondary hover:text-error"
                  data-action="click->estimate#removeItem">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-3.5 w-3.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
          <input type="hidden" name="estimate[estimate_sections_attributes][${sectionIndex}][estimate_items_attributes][${ts}][_destroy]" value="0" data-estimate-target="destroyField">
        </td>
      </tr>
    `

    tbody.insertAdjacentHTML("beforeend", html)
    this.recalculate()
  }

  removeSection(event) {
    const sectionEl = event.currentTarget.closest("[data-estimate-target='section']")
    const destroyField = sectionEl.querySelector("input[name*='[_destroy]']")

    const idField = sectionEl.querySelector("input[name*='[id]']")
    if (idField && idField.value) {
      destroyField.value = "1"
      sectionEl.style.display = "none"
    } else {
      sectionEl.remove()
    }
    this.recalculate()
  }

  removeItem(event) {
    const itemRow = event.currentTarget.closest("[data-estimate-target='item']")
    const destroyField = itemRow.querySelector("input[name*='[_destroy]']")

    const idField = itemRow.querySelector("input[name*='[id]']")
    if (idField && idField.value) {
      destroyField.value = "1"
      itemRow.style.display = "none"
    } else {
      itemRow.remove()
    }
    this.recalculate()
  }

  recalculate() {
    const sections = this.element.querySelectorAll("[data-estimate-target='section']")
    let grandTotalDays = 0
    let grandTotalCost = 0
    let totalItemCount = 0
    let visibleSectionCount = 0
    const sectionData = []

    sections.forEach(sectionEl => {
      if (sectionEl.style.display === "none") return

      visibleSectionCount++
      const items = sectionEl.querySelectorAll("[data-estimate-target='item']")
      const riskToggle = sectionEl.querySelector("[data-estimate-target='riskToggle']")
      const applyRisk = riskToggle?.checked || false

      let sectionBest = 0, sectionLikely = 0, sectionWorst = 0, sectionExpected = 0, sectionCost = 0
      let sectionItemCount = 0

      items.forEach(itemEl => {
        if (window.getComputedStyle(itemEl).display === "none") return

        sectionItemCount++
        const best = parseFloat(itemEl.querySelector("[data-estimate-target='bestCase']")?.value) || 0
        const likely = parseFloat(itemEl.querySelector("[data-estimate-target='likelyCase']")?.value) || 0
        const worst = parseFloat(itemEl.querySelector("[data-estimate-target='worstCase']")?.value) || 0

        let expected = (best + 4 * likely + worst) / 6
        if (applyRisk) expected = expected * (1 + this.riskValue)

        const cost = expected * this.dailyRateValue

        const expectedEl = itemEl.querySelector("[data-estimate-target='itemExpected']")
        const costEl = itemEl.querySelector("[data-estimate-target='itemCost']")
        if (expectedEl) expectedEl.textContent = expected.toFixed(2)
        if (costEl) costEl.textContent = this.formatCurrency(cost)

        sectionBest += best
        sectionLikely += likely
        sectionWorst += worst
        sectionExpected += expected
        sectionCost += cost
      })

      totalItemCount += sectionItemCount

      sectionEl.querySelectorAll("[data-estimate-target='sectionBest']").forEach(el => { el.textContent = sectionBest.toFixed(2) })
      sectionEl.querySelectorAll("[data-estimate-target='sectionLikely']").forEach(el => { el.textContent = sectionLikely.toFixed(2) })
      sectionEl.querySelectorAll("[data-estimate-target='sectionWorst']").forEach(el => { el.textContent = sectionWorst.toFixed(2) })
      sectionEl.querySelectorAll("[data-estimate-target='sectionExpected']").forEach(el => { el.textContent = sectionExpected.toFixed(2) })
      sectionEl.querySelectorAll("[data-estimate-target='sectionCost']").forEach(el => { el.textContent = this.formatCurrency(sectionCost) })

      grandTotalDays += sectionExpected
      grandTotalCost += sectionCost

      const nameInput = sectionEl.querySelector("input[name*='[name]']")
      sectionData.push({
        name: nameInput?.value || "Sezione",
        days: sectionExpected,
        cost: sectionCost,
        items: sectionItemCount
      })
    })

    // Enhancement contingency
    const contingencyDays = grandTotalDays * this.enhancementValue
    const contingencyCost = grandTotalCost * this.enhancementValue

    // Tax
    const taxAmount = (grandTotalCost + contingencyCost) * this.taxValue

    // Grand total
    const grandTotal = grandTotalCost + contingencyCost + taxAmount

    // Update KPI strip
    if (this.hasTotalDaysTarget) this.totalDaysTarget.textContent = grandTotalDays.toFixed(2)
    if (this.hasTotalCostTarget) this.totalCostTarget.textContent = this.formatCurrency(grandTotalCost)
    if (this.hasContingencyCostTarget) this.contingencyCostTarget.textContent = this.formatCurrency(contingencyCost)
    if (this.hasGrandTotalTarget) this.grandTotalTarget.textContent = this.formatCurrency(grandTotal)

    // Update enriched summary
    if (this.hasTotalItemsTarget) this.totalItemsTarget.textContent = totalItemCount
    if (this.hasTotalSectionsTarget) this.totalSectionsTarget.textContent = visibleSectionCount
    if (this.hasBaseDaysTarget) this.baseDaysTarget.textContent = `${grandTotalDays.toFixed(2)} gg`
    if (this.hasBaseCostTarget) this.baseCostTarget.textContent = this.formatCurrency(grandTotalCost)
    if (this.hasContingencyDaysTarget) this.contingencyDaysTarget.textContent = `+${contingencyDays.toFixed(2)} gg`
    if (this.hasContingencyCost2Target) this.contingencyCost2Target.textContent = `+${this.formatCurrency(contingencyCost)}`
    if (this.hasTaxAmountTarget) this.taxAmountTarget.textContent = this.formatCurrency(taxAmount)
    if (this.hasGrandTotalSummaryTarget) this.grandTotalSummaryTarget.textContent = this.formatCurrency(grandTotal)
    if (this.hasAvgDailyCostTarget) {
      this.avgDailyCostTarget.textContent = grandTotalDays > 0
        ? `${this.formatCurrency(grandTotal / (grandTotalDays + contingencyDays))}/gg`
        : "\u2014"
    }

    // Render visualizations
    this.renderPieChart(sectionData, grandTotalDays)
    this.renderBreakdown(sectionData, grandTotalDays, grandTotalCost)
  }

  renderPieChart(sectionData, totalDays) {
    if (!this.hasPieChartTarget) return

    if (totalDays === 0) {
      this.pieChartTarget.innerHTML = `
        <svg viewBox="0 0 200 200" class="w-full h-full">
          <circle cx="100" cy="100" r="80" fill="none" stroke="currentColor" stroke-width="24" class="text-border" />
          <text x="100" y="105" text-anchor="middle" class="text-sm fill-current text-text-secondary" font-size="14">Nessun dato</text>
        </svg>`
      if (this.hasPieLegendTarget) this.pieLegendTarget.innerHTML = ""
      return
    }

    const R = 80
    const C = 2 * Math.PI * R
    let offset = 0
    const colors = this.constructor.COLORS

    let arcs = ""
    sectionData.forEach((s, i) => {
      if (s.days <= 0) return
      const pct = s.days / totalDays
      const dashLen = pct * C
      const color = colors[i % colors.length].hex
      arcs += `<circle cx="100" cy="100" r="${R}" fill="none"
        stroke="${color}" stroke-width="24"
        stroke-dasharray="${dashLen} ${C - dashLen}"
        stroke-dashoffset="${-offset}"
        transform="rotate(-90 100 100)"
        class="transition-all duration-300" />`
      offset += dashLen
    })

    this.pieChartTarget.innerHTML = `
      <svg viewBox="0 0 200 200" class="w-full h-full">
        <circle cx="100" cy="100" r="80" fill="none" stroke="currentColor" stroke-width="24" class="text-border" />
        ${arcs}
        <text x="100" y="95" text-anchor="middle" font-size="22" font-weight="bold" class="fill-current text-text-primary">${totalDays.toFixed(1)}</text>
        <text x="100" y="115" text-anchor="middle" font-size="12" class="fill-current text-text-secondary">giorni</text>
      </svg>`

    // Legend
    if (this.hasPieLegendTarget) {
      let legend = ""
      sectionData.forEach((s, i) => {
        if (s.days <= 0) return
        const pct = (s.days / totalDays * 100).toFixed(1)
        const color = colors[i % colors.length].hex
        legend += `
          <div class="flex items-center gap-2">
            <span class="w-2.5 h-2.5 rounded-full shrink-0" style="background-color: ${color}"></span>
            <span class="text-text-secondary truncate flex-1">${this.escapeHtml(s.name)}</span>
            <span class="font-mono text-xs text-text-secondary">${pct}%</span>
          </div>`
      })
      this.pieLegendTarget.innerHTML = legend
    }
  }

  renderBreakdown(sectionData, totalDays, totalCost) {
    if (!this.hasBreakdownTarget) return

    if (totalDays === 0) {
      this.breakdownTarget.innerHTML = '<p class="text-text-secondary text-sm">Nessun dato</p>'
      return
    }

    const colors = this.constructor.COLORS

    let html = `<div class="divide-y divide-border">`
    sectionData.forEach((s, i) => {
      const pct = totalDays > 0 ? (s.days / totalDays * 100) : 0
      const costPct = totalCost > 0 ? (s.cost / totalCost * 100) : 0
      const color = colors[i % colors.length]
      html += `
        <div class="py-2.5">
          <div class="flex items-center justify-between mb-1.5">
            <div class="flex items-center gap-2 min-w-0">
              <span class="w-2.5 h-2.5 rounded-full shrink-0" style="background-color: ${color.hex}"></span>
              <span class="text-sm font-medium truncate">${this.escapeHtml(s.name)}</span>
              <span class="text-xs text-text-secondary">${s.items} voci</span>
            </div>
            <span class="text-sm font-mono font-semibold shrink-0 ml-2">${this.formatCurrency(s.cost)}</span>
          </div>
          <div class="flex items-center gap-3 ml-[18px]">
            <div class="flex-1 bg-page-deep rounded-full h-1.5">
              <div class="${color.bg} h-1.5 rounded-full transition-all duration-300" style="width: ${pct.toFixed(1)}%"></div>
            </div>
            <span class="text-xs font-mono text-text-secondary w-20 text-right">${s.days.toFixed(1)} gg &middot; ${pct.toFixed(0)}%</span>
          </div>
        </div>`
    })
    html += `</div>`

    this.breakdownTarget.innerHTML = html
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
